'use client';
import { Country } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getErrorTranslationKey, streetValidator } from '@/utils/form-rule';
import { Box } from '@mui/material';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useContext, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AddressFormData } from '.';
import AutoComplete from '../Form/AutoComplete';
import Input from '../Form/FormInput';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import WapOnly from '../Responsive/WapOnly';

export default function FormItemStreet({
  formData,
  countryList,
}: {
  formData: UseFormReturn<AddressFormData, any, AddressFormData>;
  countryList: Country[];
}) {
  const t = useTranslations('address');
  const tValidation = useTranslations('components.address.validation');
  const [addressList, setAddressList] = useState<
    Array<{
      id: number;
      description: string;
      place_id: string;
    }>
  >([]);
  const { deviceType } = useContext(ResponsiveContext);
  const [street, setStreet] = useState<string>(formData.getValues('street'));
  formData.watch((value) => {
    setStreet(value.street);
  });
  const [service, setService] = useState<any>();
  const [placeService, setPlaceService] = useState<any>();
  const [sessionToken, setSessionToken] = useState<any>();
  // 注册谷歌地址自动填充
  const initMap = () => {
    try {
      setService(new (window as any).google.maps.places.AutocompleteService());
      setPlaceService(
        new (window as any).google.maps.places.PlacesService(document.createElement('div')),
      );
      setSessionToken(new (window as any).google.maps.places.AutocompleteSessionToken());
    } catch (error) {
      logger.error(error);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  // 请求谷歌地址自动填充
  const autoComplete = (queryString: string) => {
    if (!service) {
      initMap();
      return;
    }
    setIsLoading(true);
    service.getPlacePredictions(
      {
        input: queryString,
        sessionToken,
        language: 'en',
        componentRestrictions: { country: formData.getValues('country_id').toLowerCase() },
      },
      (predictions, status) => {
        const _addressList = [];
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
          predictions.map((item, index) => {
            const termsValueArr = item.terms.slice(0, -1).map((term) => {
              return term.value;
            });
            if (termsValueArr.length > 3) {
              _addressList.push({
                id: index,
                description: termsValueArr.join(', '),
                place_id: item.place_id,
              });
              return {
                id: index,
                description: termsValueArr.join(', '),
                place_id: item.place_id,
              };
            } else {
              return '';
            }
          });
          if (formData.getValues('street')) {
            setAddressList(_addressList || []);
          } else {
            setAddressList([]);
          }
          setIsLoading(false);
        } else {
          setAddressList(_addressList || []);
          setIsLoading(false);
        }
      },
    );
  };
  // 谷歌自动地址填充请求触发
  const { run: querySearch } = useDebounceFn(
    (value: string) => {
      formData.setValue('street', value);
      formData.trigger('street');
      if (!value) {
        setAddressList([]);
        return;
      }
      autoComplete(value);
    },
    {
      wait: 1000,
    },
  );

  const onStreetSelect = (item: { id: number; description: string; place_id: string }) => {
    setAddressList([]);
    const streetList = item.description.split(', ');
    streetList.length -= 2;
    formData.setValue('street', streetList.join(', '));
    placeService.getDetails(
      {
        placeId: item.place_id,
        fields: ['address_components'],
        sessionToken,
      },
      (details) => {
        details.address_components.forEach((entry) => {
          if (entry.types[0] === 'postal_code') {
            formData.setValue('postcode', entry.long_name);
          } else if (entry.types[0] === 'locality') {
            formData.setValue('city', entry.long_name);
          } else if (entry.types[0] === 'administrative_area_level_1') {
            formData.setValue('province', entry.long_name);
            const temp = countryList.filter(
              (_item) => _item.country_id === formData.getValues('country_id'),
            );
            const temp2 = temp[0].province.filter(
              (_item) => _item.default_name === entry.long_name,
            );
            formData.setValue('province_id', temp2[0].region_id);
            formData.setValue('region_code', temp2[0].code);
          }
          formData.trigger(['street', 'province', 'postcode', 'city']);
        });
      },
    );
  };

  useEffect(() => {
    initMap();
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        '&:focus-within .FormItemAddressPlaceholder': {
          display: 'none',
        },
      }}
    >
      {['US', 'CA', 'GB'].includes(formData.getValues('country_id')) ? (
        <AutoComplete
          name="street"
          freeSolo
          label={
            [EPlatform.WEB].includes(deviceType) ? t('addressWebLabel') : t('addressMobileLabel')
          }
          control={formData.control}
          options={addressList.map((item) => item.description)}
          disableClearable
          multiple={false}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') {
              querySearch(value);
            }
          }}
          onChange={(_, value, reason) => {
            if (reason === 'selectOption') {
              onStreetSelect(addressList.find((item) => item.description === value));
            }
          }}
          optionTextIsEllipsis
          blurOnSelect
          rules={{
            validate: () => {
              const errorCode = streetValidator(formData.getValues('street'));
              if (errorCode) {
                const translationKey = getErrorTranslationKey(errorCode);
                return translationKey ? tValidation(translationKey) : 'Validation error';
              }
              return true;
            },
          }}
          loading={isLoading}
        />
      ) : (
        <Input
          name="street"
          label={
            [EPlatform.WEB].includes(deviceType) ? t('addressWebLabel') : t('addressMobileLabel')
          }
          control={formData.control}
          slotProps={{
            input: {
              inputProps: {
                maxLength: 50,
              },
            },
          }}
          rules={{
            validate: () => {
              const errorCode = streetValidator(formData.getValues('street'));
              if (errorCode) {
                const translationKey = getErrorTranslationKey(errorCode);
                return translationKey ? tValidation(translationKey) : 'Validation error';
              }
              return true;
            },
          }}
        />
      )}
      <WapOnly>
        {!street && (
          <div className="FormItemAddressPlaceholder absolute top-[8px] left-[96px] h-[46px] f14 right-[16px] flex items-center text-auxiliary-dark-gray pointer-events-none">
            {t('addressPlaceholder')}
          </div>
        )}
      </WapOnly>
    </Box>
  );
}
